import { ColumnDef } from "@tanstack/react-table";
import useDataTable from "hooks/useDataTable";
import {
    IndustryEngagementSliceState,
    setIndustryEngagementCount,
    setIndustryEngagements,
    setIndustryEngagementSearchFields,
    deleteIndustryEngagement
} from 'slices/business-development/industry-engagement/reducer'
import { IndustryEngagement, IIndustryEngagementQueryParams } from "models/industry-engagement";
import React, { useCallback, useEffect, useState } from "react";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import DataTableTemplate from "Components/templates/data-table-template";
import { getIndustryEngagements, getIndustryEngagementSearchFields } from "slices/thunk";
import { deleteIndustryEngagementRequest } from "slices/business-development/industry-engagement/thunk";
import AreYouSureModal from "Components/organisms/are-you-sure-modal";
import Employee from "models/employee";
import Opportunity from "models/opportunity";
import ActionButton from "Components/atoms/buttons/action-button";
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container";
import IconButton from "Components/atoms/buttons/icon-button";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { navigateToCreateIndustryEngagement, navigateToEditIndustryEngagement } from "utils/navigator";
import withRouter from "Components/Common/withRouter";
import { IndustryEngagementStatus, IndustryEngagementUI } from "models/industry-engagement";
import SearchIndustryEngagementForm from "Components/organisms/forms/search-industry-engagement-form";
import { PlusIcon, PencilIcon, TrashIcon, HelpIcon } from "Components/atoms/icons";
import useManual from "hooks/useManual";

const selectIndustryEngagement = createSelector(
    (state: any) => state.IndustryEngagement,
    ({
        industryEngagementCount,
        industryEngagements,
        employees,
        statuses,
        opportunities,
    }: IndustryEngagementSliceState) => ({
        industryEngagementCount,
        industryEngagements,
        employees,
        statuses,
        opportunities,
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({ employee })
)

const DEFAULT_SEARCH: IIndustryEngagementQueryParams = {
    page: 0,
    limit: Number.MAX_SAFE_INTEGER,
    statusIds: [],
    attendeeIds: [],
    opportunityIds: [],
    dateFrom: undefined,
    dateTo: undefined,
}

const IndustryEngagementPage = (props: any) => {
    document.title = "Industry Engagement | IbexIQ";
    const { handleOpenManual } = useManual("business-development/industry-engagement");
    const [query, setQuery] = useState<IIndustryEngagementQueryParams>(DEFAULT_SEARCH);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [focusedEngagementId, setFocusedEngagementId] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const dispatch: any = useDispatch();

    const {
        industryEngagementCount,
        industryEngagements,
        employees,
        statuses,
        opportunities,
    } = useSelector(selectIndustryEngagement)

    const { employee } = useSelector(selectProfile)

    const fetchIndustryEngagements = async (q: IIndustryEngagementQueryParams) => {
        setIsLoading(true)
        const res = await getIndustryEngagements(q)

        if (!res) {
            setIsLoading(false)
            return;
        }

        dispatch(setIndustryEngagements(res.data))
        dispatch(setIndustryEngagementCount(res.count))
        setIsLoading(false)
    }

    const handleChangeQuery = <K extends keyof IIndustryEngagementQueryParams>(
        key: K,
        value: IIndustryEngagementQueryParams[K]
    ) => {
        if (key !== 'page' && key !== 'limit') {
            query.page = 0;
            query.limit = Number.MAX_SAFE_INTEGER
        }
        const q = { ...query, [key]: value }
        setQuery(q)
    }

    const handleClearSearch = useCallback(() => {
        const q = { ...DEFAULT_SEARCH }
        setQuery(q)
        fetchIndustryEngagements(q)
    }, [setQuery, fetchIndustryEngagements])

    const handleAddIndustryEngagement = () => navigateToCreateIndustryEngagement(props.router.navigate)
    const handleEditIndustryEngagement = (engagement: IndustryEngagement) => navigateToEditIndustryEngagement(props.router.navigate, engagement.id)

    const handleDeleteIndustryEngagement = (engagement: IndustryEngagement) => {
        setFocusedEngagementId(engagement.id);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = async () => {
        setIsDeleteModalOpen(false);
        setFocusedEngagementId(null);
    };

    const confirmDeleteIndustryEngagement = useCallback(async () => {
        setIsDeleting(true);
        if (!focusedEngagementId) return;
        const success = await deleteIndustryEngagementRequest(focusedEngagementId);
        if (success) {
            dispatch(deleteIndustryEngagement(focusedEngagementId));
            handleCloseDeleteModal();
        }
        setIsDeleting(false);
    }, [focusedEngagementId]);

    const initializePage = async () => {
        if (employees.length === 0 || statuses.length === 0 || opportunities.length === 0) {
            const res = await getIndustryEngagementSearchFields()
            if (!res) return;
            dispatch(setIndustryEngagementSearchFields(res))
        }

        if (industryEngagements.length === 0) {
            await fetchIndustryEngagements(query)
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [])

    const columns = React.useMemo<ColumnDef<IndustryEngagement>[]>(() => [
        {
            header: "Event",
            accessorKey: "name",
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 100,
            cell: ({ row }) => {
                const isOwner = row.original.createdById === employee?.id
                return <TableRowControlsContainer
                    style={{ justifyContent: 'space-between' }}
                >
                    {row.original.name}
                    {
                        isOwner &&
                        <div className='d-flex flex-row justify-content-end'>
                            <IconButton onClick={() => handleEditIndustryEngagement(row.original)}><PencilIcon /></IconButton>
                            <IconButton onClick={() => handleDeleteIndustryEngagement(row.original)}><TrashIcon /></IconButton>
                        </div>
                    }
                </TableRowControlsContainer>
            }
        },
        {
            header: "Booking Status",
            id: "status",
            accessorFn: (row) => row.status?.description,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 80,
            cell: ({ row }) => {
                const description = row.original.status?.description
                return <span className={IndustryEngagementUI.statusColor(description)}>
                    {description ?? "—"}
                </span>
            }
        },
        {
            header: "Date",
            id: "date",
            accessorFn: (row) => row.date ? row.date.toString() : undefined,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 80,
            cell: ({ row }) => row.original.date
                ? row.original.date.toString().split("T")[0]
                : "—"
        },
        {
            header: "Opportunity",
            id: "opportunities",
            accessorFn: (row) => row.opportunities?.length ? row.opportunities.map(o => o.description).join(", ") : undefined,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 150,
            cell: ({ row }) => row.original.opportunities?.length
                ? row.original.opportunities.map(o => o.description).join(", ")
                : "—"
        },
        {
            header: "Attendees",
            id: "attendees",
            accessorFn: (row) => row.attendees?.length
                ? row.attendees.map(a => a.employee?.commonName ?? `${a.employee?.firstName} ${a.employee?.lastName}`).join(", ")
                : undefined,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 150,
            cell: ({ row }) => row.original.attendees?.length
                ? row.original.attendees.map(a =>
                    a.employee?.commonName ?? `${a.employee?.firstName} ${a.employee?.lastName}`
                ).join(", ")
                : "—"
        }
    ], [])

    const table = useDataTable({
        columns,
        data: industryEngagements,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title={"Business Development"}
        breadcrumbItem={"Industry Engagement"}
        table={table}
        columns={columns}
        isLoading={isLoading}
        rowCount={industryEngagementCount}
        modal={<AreYouSureModal
            isOpen={isDeleteModalOpen}
            onModalClose={handleCloseDeleteModal}
            modalTitle="Delete Industry Engagement"
            handleAreYouSureAccept={confirmDeleteIndustryEngagement}
            handleAreYouSureDecline={handleCloseDeleteModal}
            areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
            areYouSuredeclineText="Cancel"
        />}
        actions={<>
            <ActionButton onClick={handleAddIndustryEngagement}>
                Add <PlusIcon />
            </ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        filters={<SearchIndustryEngagementForm
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            handleSearch={() => fetchIndustryEngagements(query)}
            statuses={[{ value: '', label: 'All' }, ...statuses.map((s: IndustryEngagementStatus) => ({ value: s.id, label: s.description }))]}
            attendees={[{ value: '', label: 'All' }, ...employees.map((e: Employee) => ({ value: e.id, label: e.commonName ?? `${e.firstName} ${e.lastName}` }))]}
            opportunities={[{ value: '', label: 'All' }, ...opportunities.map((o: Opportunity) => ({ value: o.id, label: o.description }))]}
            query={query}
            isSearching={isLoading}
        />}
    />
}

export default withRouter(IndustryEngagementPage)